import { useTranslation } from 'react-i18next';
import { PipelineStatusBadge } from './PipelineStatusBadge.js';
import { PipelineTypeBadge } from './PipelineTypeBadge.js';
import type { PipelineConfig } from '../../../types/api.js';

export interface PipelineDetailPanelProps {
  /** 表示するパイプライン設定（null時は非表示） */
  config: PipelineConfig | null;
  /** パネルの開閉状態 */
  isOpen: boolean;
  /** 閉じるハンドラー */
  onClose: () => void;
}

/**
 * @graph-stack cortex-admin
 * @graph-domain Infrastructure
 * @graph-business パイプライン詳細パネル。右スライドインでジョブ情報・SQL・Cron・メタデータを構造化表示
 * @graph-connects PipelineStatusBadge [calls] ステータスバッジ描画
 * @graph-connects PipelineTypeBadge [calls] タイプバッジ描画
 */
export function PipelineDetailPanel({ config, isOpen, onClose }: PipelineDetailPanelProps) {
  const { t } = useTranslation();

  if (!config) return null;

  return (
    <>
      {/* オーバーレイ */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-[var(--z-modal)]"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      {/* パネル */}
      <div
        className={`
          fixed top-0 right-0 h-full w-full sm:w-[28rem] z-[var(--z-modal)]
          bg-[var(--color-bg-surface)] border-l border-[var(--color-border)]
          transition-transform duration-300 ease-out
          ${isOpen ? 'translate-x-0' : 'translate-x-full'}
        `}
        role="dialog"
        aria-modal="true"
        aria-label={`${t('pipeline.detail')}: ${config.jobName}`}
      >
        {/* ヘッダー */}
        <div className="flex items-center justify-between p-4 border-b border-[var(--color-border)]">
          <h2 className="text-lg font-semibold text-[var(--color-text-primary)] truncate">
            {t('pipeline.detail')}
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-hover)] transition-colors"
            aria-label={t('common.close')}
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* コンテンツ */}
        <div className="overflow-y-auto h-[calc(100%-4rem)] p-4 space-y-5">
          {/* ジョブ名 */}
          <div>
            <p className="text-xs text-[var(--color-text-muted)] mb-1">{t('pipeline.jobName')}</p>
            <p className="text-sm font-medium text-[var(--color-text-primary)]">{config.jobName}</p>
          </div>

          {/* バッジ行 */}
          <div className="flex items-center gap-3">
            <PipelineTypeBadge type={config.type} />
            <PipelineStatusBadge status={config.status} />
          </div>

          {/* Cron式 */}
          <div>
            <p className="text-xs text-[var(--color-text-muted)] mb-1">{t('pipeline.cron')}</p>
            <code className="block px-3 py-2 rounded-lg bg-[var(--color-bg-elevated)] text-sm font-mono text-[var(--color-text-primary)] border border-[var(--color-border)]">
              {config.cronExpression}
            </code>
          </div>

          {/* 対象DB */}
          {config.targetDb && (
            <div>
              <p className="text-xs text-[var(--color-text-muted)] mb-1">
                {t('pipeline.targetDb')}
              </p>
              <p className="text-sm text-[var(--color-text-primary)]">{config.targetDb}</p>
            </div>
          )}

          {/* 書き込みモード */}
          {config.writeMode && (
            <div>
              <p className="text-xs text-[var(--color-text-muted)] mb-1">
                {t('pipeline.writeMode')}
              </p>
              <p className="text-sm text-[var(--color-text-primary)]">{config.writeMode}</p>
            </div>
          )}

          {/* Dataset / Table */}
          {(config.datasetId ?? config.tableId) && (
            <div>
              <p className="text-xs text-[var(--color-text-muted)] mb-1">Dataset / Table</p>
              <p className="text-sm text-[var(--color-text-primary)]">
                {[config.datasetId, config.tableId].filter(Boolean).join('.')}
              </p>
            </div>
          )}

          {/* SQLクエリ */}
          {config.sqlQuery && (
            <div>
              <p className="text-xs text-[var(--color-text-muted)] mb-1">
                {t('pipeline.sqlQuery')}
              </p>
              <pre className="block px-3 py-3 rounded-lg bg-[var(--color-bg-elevated)] text-xs font-mono text-[var(--color-text-primary)] border border-[var(--color-border)] overflow-x-auto whitespace-pre-wrap break-words max-h-64">
                {config.sqlQuery}
              </pre>
            </div>
          )}

          {/* メタデータ */}
          <div className="pt-3 border-t border-[var(--color-border)] space-y-2">
            <div className="flex justify-between text-xs">
              <span className="text-[var(--color-text-muted)]">Created by</span>
              <span className="text-[var(--color-text-secondary)]">{config.createdBy}</span>
            </div>
            {config.createdAt && (
              <div className="flex justify-between text-xs">
                <span className="text-[var(--color-text-muted)]">Created at</span>
                <span className="text-[var(--color-text-secondary)]">{config.createdAt}</span>
              </div>
            )}
            {config.updatedAt && (
              <div className="flex justify-between text-xs">
                <span className="text-[var(--color-text-muted)]">{t('pipeline.lastUpdated')}</span>
                <span className="text-[var(--color-text-secondary)]">{config.updatedAt}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
